import { faCircleCheck, faCircleX } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { alpha, Button, Chip, Paper, Stack, Typography } from '@mui/material'
import { DirectDealCategory, DirectDealStatus } from '@services/directDeals.types'
import { numToCurrency, numToPercentage } from '@utils/stringHelpers'

type Props = {
	id: Id
	selected: boolean
	category: DirectDealCategory
	manufacturer: string
	customer: string
	stocking: number
	coverage: number
	totalSavings: number
	currency: string
	status: DirectDealStatus
	onClick: () => void
}

const CategoryProps = {
	[DirectDealCategory.RECOMMEND]: {
		label: 'Recommend',
		color: 'success.dark',
		bgcolor: (theme) => alpha(theme.palette.success.dark, 0.08),
	},
	[DirectDealCategory.DECLINE]: {
		label: 'Decline',
		color: 'error.dark',
		bgcolor: (theme) => alpha(theme.palette.error.dark, 0.08),
	},
}

export default function DirectDealCard({
	id,
	selected,
	category,
	manufacturer,
	customer,
	stocking,
	coverage,
	totalSavings,
	currency,
	status,
	onClick,
}: Props) {
	return (
		<Paper
			elevation={1}
			onClick={!selected ? onClick : null}
			data-testid={`direct-deals-card-${id}`}
			sx={{
				width: '100%',
				height: 'fit-content',
				borderRadius: 2,
				border: selected ? '2px solid' : '1px solid',
				borderColor: selected ? (theme) => theme.palette.info.main : 'divider',
				bgcolor: selected ? (theme) => alpha(theme.palette.info.main, 0.08) : 'background.paper',
				p: 2,
				cursor: 'pointer',
			}}
		>
			<Stack spacing={1}>
				<Stack direction="row" justifyContent="space-between" alignItems="flex-start">
					<Stack spacing={0.2}>
						<Typography variant="body2" fontWeight="bold">
							{manufacturer}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{customer}
						</Typography>
						<Stack direction="row" spacing={1}>
							<Chip
								label={CategoryProps[category].label}
								size="small"
								sx={{
									width: 'fit-content',
									color: CategoryProps[category].color,
									bgcolor: CategoryProps[category].bgcolor,
								}}
							/>
							{status === DirectDealStatus.ACCEPTED && (
								<Chip
									label="Accepted"
									size="small"
									icon={<FontAwesomeIcon icon={faCircleCheck} color="success.dark" />}
									sx={{
										width: 'fit-content',
										color: CategoryProps.RECOMMEND.color,
										bgcolor: CategoryProps.RECOMMEND.bgcolor,
									}}
								/>
							)}
							{status === DirectDealStatus.REJECTED && (
								<Chip
									label="Declined"
									size="small"
									icon={<FontAwesomeIcon icon={faCircleX} color="error.dark" />}
									sx={{
										width: 'fit-content',
										color: CategoryProps.DECLINE.color,
										bgcolor: CategoryProps.DECLINE.bgcolor,
									}}
								/>
							)}
						</Stack>
					</Stack>
					<Button
						sx={{ p: 1 }}
						variant={selected ? 'contained' : 'outlined'}
						size="small"
						onClick={!selected ? onClick : null}
						data-testid={`direct-deals-card-select-${id}`}
					>
						{selected ? 'Selected' : 'Select'}
					</Button>
				</Stack>
				<Stack direction="row" spacing={3} justifyContent="space-between">
					<Stack>
						<Typography variant="caption" fontWeight="bold" color="text.secondary">
							Coverage
						</Typography>
						<Typography variant="body2" fontWeight="bold" color={CategoryProps[category].color}>
							{numToPercentage(coverage / 100, 0)}
						</Typography>
					</Stack>
					<Stack>
						<Typography variant="caption" fontWeight="bold" color="text.secondary">
							Stocked
						</Typography>
						<Typography variant="body2" fontWeight="bold" color={CategoryProps[category].color}>
							{numToPercentage(stocking / 100, 0)}
						</Typography>
					</Stack>
					<Stack>
						<Typography variant="caption" fontWeight="bold" color="text.secondary">
							Total Savings
						</Typography>
						<Typography variant="body2" fontWeight="bold" color={CategoryProps[category].color}>
							{numToCurrency(totalSavings, currency, 0)}
						</Typography>
					</Stack>
				</Stack>
			</Stack>
		</Paper>
	)
}
